import { inject, Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
    BrokerSignalStore,
    ButtonClick,
    CustomPageToolbarResolver,
    CustomPageType,
    ExaminationStatusForImaging,
    ICustomPageToolbarResolver,
    ImagingService,
    IModalDialogNotification,
    isKeyImageNotFoundFault,
    KeyImagesService,
    ModalDialogNotificationService,
    NotificationService,
    NotificationType,
    RootState,
    seExaminationAssociation,
    selectSeReportKeyImages,
    seReportAddKeyImages,
    seReportDeleteKeyImages,
    seReportSetExaminationStudies,
    seReportSetImagesPerRowInPlaceholder,
    seReportSetKeyImages,
    seReportSetPatientId,
    seViewerKeyImageAdd,
    seViewerKeyImageRefresh,
    seViewerKeyImagesDelete,
    seViewerKeyImagesPerRows,
    seViewerKeyImagesSet,
    UserConfigurationSettingsService,
} from '@se/platform';
import { Logger } from '@se/se.platform.base';
import { intersectionBy, isEqual } from 'lodash-es';
import {
    catchError,
    combineLatest,
    distinctUntilChanged,
    EMPTY,
    exhaustMap,
    filter,
    forkJoin,
    from,
    map,
    mergeMap,
    of,
    pairwise,
    startWith,
    Subscription,
    switchMap,
    take,
    tap,
    withLatestFrom,
} from 'rxjs';

import { HttpErrorResponse } from '@angular/common/http';
import { EWebViewerType } from 'estensa-module';
import { selectViewerConnected } from 'projects/se-modules-platform/src/lib/connectivity/connectivity.selectors';
import { isNotNullOrUndefined } from '../cp-configuration/utils/utils';
import { isEntity } from '../cp-configuration/view-model';
import { CpReportService, ReportStatus } from '../cp-report.service';
import {
    cpEmpty,
    cpInitialize,
    cpOpenImagesForCurrentExamination,
    cpSafeExit,
    cpSave,
    cpShowErrorModal,
    cpShowWarningModal,
} from './cp.actions';
import { CpEffects } from './cp.effects';
import { selectCpModules, selectCpModulesAndImaging } from './cp.selectors';

export function reportStatusToImagingStatus(
    oldStatus: ReportStatus | undefined,
    newStatus: ReportStatus | undefined,
): ExaminationStatusForImaging {
    if (!newStatus) {
        return 'Unknown';
    }
    // Stato iniziale
    if (!oldStatus) {
        if (newStatus.isApproved || newStatus.isDigitallySigned) {
            return 'Closed';
        }
        return 'Open';
    }
    // Firma o definitivo
    if (
        (!oldStatus.isApproved || !oldStatus.isDigitallySigned) &&
        (newStatus.isApproved || newStatus.isDigitallySigned)
    ) {
        return 'Close';
    }
    // Annulla firma o definitivo
    if (
        (oldStatus.isApproved || oldStatus.isDigitallySigned) &&
        (!newStatus.isApproved || !newStatus.isDigitallySigned)
    ) {
        return 'ReOpened';
    }

    return 'Open';
}

@Injectable()
export class CpImagingEffects {
    private static readonly AUTO_OPENABLE_VIEWERS = [
        EWebViewerType.EstensaReview,
        EWebViewerType.EstensaReviewVNext,
        EWebViewerType.eUnity,
        EWebViewerType.SeWebViewer,
        EWebViewerType.ZEfiRO,
        EWebViewerType.VueClient,
    ];

    initializePage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpInitialize),
            withLatestFrom(this.store.pipe(select(selectCpModulesAndImaging))),
            switchMap(([_action, { modules, imaging }]) => {
                try {
                    this.registerModelListeners();
                } catch (err) {
                    if (err instanceof Error) {
                        this.logger.error(`Failed to initialize imaging: ${err.message}`);
                    }
                    return EMPTY;
                }

                // Continua solo se è abilitato il modulo di imaging, o se la prestazione è US (per retrocompatibilità).
                if ((!modules || !modules.includes('Us')) && !imaging) {
                    return EMPTY;
                }

                return this.customPageToolbarResolver.getToolbar();
            }),
            switchMap((customPageToolbar) => {
                const cpType = customPageToolbar?.config.type;

                return forkJoin([from(this.imagingService.getViewerTypeByCp(cpType)), of(cpType)]);
            }),
            map(([viewerType, customPageType]) => {
                if (
                    CpImagingEffects.AUTO_OPENABLE_VIEWERS.includes(viewerType) &&
                    this.userConfigurationSettingsService.isAuthorized(['viewer_Get'], 'single')
                ) {
                    return cpOpenImagesForCurrentExamination({ customPageType });
                }
                return cpEmpty();
            }),
        ),
    );

    /**
     * Effettua la pulizia dei vari servizi.
     */
    safeExit$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(cpSafeExit),
                tap(() => this.safeExit()),
            ),
        { dispatch: false },
    );

    /**
     * Notifica l'utente di un'avvenuta associazione in background.
     */
    examinationAssociation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(seExaminationAssociation),
            // Mostra la modale solo se si sta refertando l'examination notificata.
            filter(({ idExamination }) =>
                isEntity(this.reportService.examinations?.find((ex) => ex.Id === idExamination)),
            ),
            switchMap(() => this.reportService.canSave$.pipe(take(1))),
            exhaustMap((canSave) => {
                if (canSave) {
                    return this.askForReload(true);
                }

                return this.notifyAssociation();
            }),
        ),
    );

    /**
     * Notifica dal viewer di una nuova immagine chiave.
     */
    viewerKeyImageAdd$ = createEffect(() =>
        this.actions$.pipe(
            ofType(seViewerKeyImageAdd),
            filter((image) => this.reportService.pacsTupleMatches(image.pacsTuple)),
            map((image) => seReportAddKeyImages({ keyImages: [image.image] })),
        ),
    );

    /**
     * Notifica dal viewer di una cancellazione di immagini chiave.
     */
    viewerKeyImageDelete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(seViewerKeyImagesDelete),
            mergeMap(({ images }) => {
                const imagesMatching = images.filter((pair) => this.reportService.pacsTupleMatches(pair.pacsTuple));

                return imagesMatching.length > 0 ? of(imagesMatching.map((pair) => pair.image)) : EMPTY;
            }),
            map((images) => {
                try {
                    this.reportService.deleteKeyImages(images);
                    return seReportSetKeyImages({ keyImages: this.reportService.keyImages });
                } catch (err: any) {
                    if (err instanceof Error) {
                        this.logger.error(`Failed to delete key image: ${err.message}`);
                    }
                    let error = err instanceof HttpErrorResponse ? err.error : err;
                    if (err && isKeyImageNotFoundFault(error)) {
                        this.notificationService.warning(
                            this.translate.instant('KeyImageNotFoundInRepository'),
                            this.translate.instant('KeyImageNotDeletable'),
                        );
                        return cpShowWarningModal({
                            title: 'KeyImages',
                            message: 'KeyImageNotFoundInRepository',
                        });
                    } else {
                        this.notificationService.error(
                            this.translate.instant('KeyImageDeleteError'),
                            this.translate.instant('KeyImages'),
                        );
                        return cpShowErrorModal({
                            title: 'KeyImages',
                            message: 'KeyImageDeleteError',
                        });
                    }
                }
            }),
        ),
    );

    /**
     * Notifica dal viewer di un aggiornamento di un'immagine chiave.
     */
    viewerKeyImageRefresh$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seViewerKeyImageRefresh),
                filter(({ pacsTuple }) => this.reportService.pacsTupleMatches(pacsTuple)),
                tap(({ image }) => {
                    try {
                        this.reportService.updateKeyImage(image);
                    } catch (err) {
                        if (err instanceof Error) {
                            this.logger.error(`Failed to refresh key image: ${err.message}`);
                        }
                    }
                }),
            ),
        { dispatch: false },
    );

    /**
     * Notifica dal viewer del cambio delle immagini chiave.
     * Effettua il merge delle keyImages dal viewer con quelle esistenti nel modello (da getData).
     */
    viewerKeyImagesSet$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seViewerKeyImagesSet),
                mergeMap(({ images }) => {
                    const imagesMatching = images.filter((pair) => this.reportService.pacsTupleMatches(pair.pacsTuple));

                    return imagesMatching.length > 0 ? of(imagesMatching.map((pair) => pair.image)) : EMPTY;
                }),
                tap((viewerImages) => {
                    try {
                        // Recupera le keyImages esistenti dal modello (caricate da getData)
                        const existingImages = this.reportService.keyImages;

                        // Merge: inizia con le immagini esistenti, poi aggiorna con quelle del viewer
                        // Usa RepositoryKey come identificatore univoco - le immagini del viewer hanno precedenza
                        const mergedImagesMap = new Map<string, typeof viewerImages[0]>();

                        // Prima, aggiungi tutte le immagini esistenti alla mappa
                        for (const img of existingImages) {
                            if (img.RepositoryKey) {
                                mergedImagesMap.set(img.RepositoryKey, img);
                            }
                        }

                        // Poi, add/update con le immagini del viewer (il viewer ha precedenza)
                        for (const img of viewerImages) {
                            if (img.RepositoryKey) {
                                mergedImagesMap.set(img.RepositoryKey, img);
                            }
                        }

                        const mergedImages = Array.from(mergedImagesMap.values());
                        this.reportService.setKeyImages(mergedImages);
                    } catch (err) {
                        if (err instanceof Error) {
                            this.logger.error(`Failed to set key images: ${err.message}`);
                        }
                    }
                }),
            ),
        { dispatch: false },
    );

    /**
     * Aggiunta di immagini chiave all'interno della refertazione.
     */
    reportKeyImageAdd$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seReportAddKeyImages),
                tap(({ keyImages: images }) => {
                    try {
                        for (const image of images) {
                            this.reportService.addKeyImage(image);
                        }
                    } catch (err) {
                        if (err instanceof Error) {
                            this.logger.error(`Failed to add key image: ${err.message}`);
                        }
                    }
                }),
            ),
        { dispatch: false },
    );

    /**
     * Cancellazione di immagini chiave all'interno della refertazione.
     */
    reportKeyImageDelete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(seReportDeleteKeyImages),
            exhaustMap(({ keyImages }) => {
                return this.keyImagesService.deleteKeyImages(keyImages.map((img) => img.RepositoryKey)).pipe(
                    tap(() => this.reportService.deleteKeyImages(keyImages)),
                    exhaustMap(() => this.cpEffects.save()),
                    exhaustMap(() => from(this.imagingService.deleteKeyImages(keyImages))),
                    map(() => seReportSetKeyImages({ keyImages: this.reportService.keyImages })),
                    catchError((err) => {
                        if (err instanceof Error) {
                            this.logger.error(`Failed to add key image: ${err.message}`);
                        }
                        let error = err instanceof HttpErrorResponse ? err.error : err;

                        if (error && isKeyImageNotFoundFault(error)) {
                            this.notificationService.warning(
                                this.translate.instant('KeyImageNotFoundInRepository'),
                                this.translate.instant('KeyImageNotDeletable'),
                            );
                            return of(
                                cpShowWarningModal({
                                    title: 'KeyImages',
                                    message: 'KeyImageNotFoundInRepository',
                                }),
                            );
                        } else {
                            this.notificationService.error(
                                this.translate.instant('KeyImageDeleteError'),
                                this.translate.instant('KeyImages'),
                            );
                            return of(
                                cpShowErrorModal({
                                    title: 'KeyImages',
                                    message: 'KeyImageDeleteError',
                                }),
                            );
                        }
                    }),
                );
            }),
        ),
    );

    /**
     * Cambio di disposizione delle immagini chiave.
     */
    viewerImagesPerRowInPlaceholder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(seViewerKeyImagesPerRows),
            map(({ rows }) => {
                try {
                    const imagesPerRow = rows ?? 1;
                    this.reportService.setImagesPerRowInPlaceholder(imagesPerRow);

                    return seReportSetImagesPerRowInPlaceholder({ imagesPerRowInPlaceholder: imagesPerRow });
                } catch (err) {
                    if (err instanceof Error) {
                        this.logger.error(`Failed to add images per row in placeholder: ${err.message}`);
                    }

                    return cpEmpty();
                }
            }),
        ),
    );

    /**
     * Apertura delle immagini per l'examination corrente.
     */
    openImages$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(cpOpenImagesForCurrentExamination),
                exhaustMap(({ customPageType }) =>
                    from(this.openImagesForCurrentExamination(customPageType)).pipe(
                        catchError((err) => {
                            if (err instanceof Error) {
                                this.logger.error(`Failed to open images: ${err.message}`);
                            }
                            return of(cpEmpty());
                        }),
                    ),
                ),
            ),
        { dispatch: false },
    );

    private modelSubscriptions: Subscription[] = [];

    private readonly examinationStatus$ = () =>
        this.reportService.reportStatus$.pipe(
            // Inizia con undefined per forzare la notifica del primo stato.
            startWith(undefined),
            // Prendi l'ultimo stato e quello attuale.
            pairwise(),
            // Mappa lo stato del referto in uno stato di imaging.
            map(([oldStatus, newStatus]) => reportStatusToImagingStatus(oldStatus, newStatus)),
            // Recupera le examination.
            withLatestFrom(this.reportService.examinationIds$),
            // Notifica nuovi stati solo se ci sono examination.
            filter(([_, examinationIds]) => examinationIds.length > 0),
            // Notifica solo se lo stato cambia.
            distinctUntilChanged(isEqual),
        );

    private readonly usExamination$ = () =>
        this.store.pipe(
            // Nel caso l'esame aperto è un US...
            select(selectCpModules),
            filter((mods) => mods.includes('Us')),
            // Quando cambia la ReferenceExamination...
            switchMap(() => this.reportService.referenceExaminationId$),
            // E se non è nulla (la pagina è stata chiusa).
            filter(isNotNullOrUndefined),
            distinctUntilChanged(),
        );

    private readonly keyImages$ = () =>
        this.reportService.keyImages$.pipe(
            withLatestFrom(this.store.pipe(select(selectSeReportKeyImages))),
            filter(([imagesFromReport, imagesFromStore]) => {
                // Notifichiamo una modifica solo se ci sono effettivamente delle immagini modificate.
                // Quindi se il numero di immagini è diverso o se ci sono immagini nel report che non sono
                //   nello store.
                if (imagesFromReport.length !== imagesFromStore.length) {
                    return true;
                }
                const intersection = intersectionBy(imagesFromReport, imagesFromStore, 'RepositoryKey');
                return intersection.length !== imagesFromReport.length;
            }),
        );

    brokerStore = inject(BrokerSignalStore);

    constructor(
        private actions$: Actions,
        private cpEffects: CpEffects,
        private imagingService: ImagingService,
        private keyImagesService: KeyImagesService,
        private logger: Logger,
        private modalDialogNotificationService: ModalDialogNotificationService,
        private reportService: CpReportService,
        private store: Store<RootState>,
        private translate: TranslateService,
        private notificationService: NotificationService,
        private userConfigurationSettingsService: UserConfigurationSettingsService,
        @Inject(CustomPageToolbarResolver) private customPageToolbarResolver: ICustomPageToolbarResolver,
    ) {}

    safeExit() {
        try {
            this.cleanupModelListeners();
            this.imagingService.discardImages();
            this.store.dispatch(seReportSetPatientId({ patientId: null }));
            this.store.dispatch(seReportSetKeyImages({ keyImages: [] }));
        } catch {
            // Non far nulla per ora.
        }
    }

    // #region Helpers

    private askForReload(forceOk = false) {
        const notification: IModalDialogNotification = {
            buttonCancel: false,
            buttonNo: !forceOk,
            buttonOk: forceOk,
            buttonYes: !forceOk,
            message: this.translate.instant('NewAssociationReload'),
            title: 'Warning',
            type: NotificationType.warning,
        };

        // Mappa il pulsante della modale ad un'azione da scatenare.
        return this.modalDialogNotificationService.notify(notification).pipe(
            take(1),
            map((btn) => (btn === ButtonClick.yes || forceOk ? cpSave({ forcedReload: true }) : cpEmpty())),
        );
    }

    private notifyAssociation() {
        const notification: IModalDialogNotification = {
            buttonCancel: false,
            buttonNo: false,
            buttonOk: true,
            buttonYes: false,
            message: this.translate.instant('NewAssociationAvailable'),
            title: 'Warning',
            type: NotificationType.warning,
        };

        return this.modalDialogNotificationService.notify(notification).pipe(
            take(1),
            map(() => cpEmpty()),
        );
    }

    private cleanupModelListeners() {
        // Sincronizza lo stato del modello e quello dello store del report.
        for (const sub of this.modelSubscriptions) {
            sub.unsubscribe();
        }
        this.modelSubscriptions = [];
    }

    private registerModelListeners() {
        this.modelSubscriptions.push(
            this.keyImages$().subscribe(([images]) => {
                this.store.dispatch(seReportSetKeyImages({ keyImages: images }));
            }),
        );

        this.store.pipe(select(selectSeReportKeyImages)).subscribe((images) => {
            this.reportService.setKeyImages(images);
        });

        // Synchronize examination studies from CpReportService to the store
        this.modelSubscriptions.push(
            this.reportService.examinations$.pipe(distinctUntilChanged(isEqual)).subscribe(() => {
                this.store.dispatch(seReportSetExaminationStudies({ examinationStudies: this.reportService.examinationStudies }));
            }),
        );

        // È necessario comunicare al viewer il cambiamento dello stato del referto.
        this.modelSubscriptions.push(
            combineLatest([
                this.examinationStatus$(),
                this.store.pipe(select(selectViewerConnected)),
                this.reportService.studyInstanceUIDs$,
            ]).subscribe(([[status, examinationIds], isViewerConnected, studyInstanceUIDs]) => {
                if (!isViewerConnected) {
                    return;
                }

                this.imagingService.setExaminationStatus(examinationIds, status, (studyInstanceUIDs || []) as string[]);
            }),
        );
    }

    private async openImagesForCurrentExamination(customPageType: CustomPageType | undefined): Promise<void> {
        const examinationEntity = this.reportService.referenceExamination;
        if (!examinationEntity) {
            return;
        }
        this.brokerStore.openBrokerFromExternal({
            examinationIds: this.reportService.examinationIds ?? [],
            customPageType: customPageType,
        });
    }

    // #endregion
}
